import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {DelegateVerticalScrollingPosition} from '@aviary/dialogs/base-dialog/base-dialog.types';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {NgClass} from '@angular/common';

const END_PADDING_CLASS = 'pe-[var(--mx-dialog-body-padding-x)]';
const DELEGATE_VERTICAL_SCROLLING_CLASS = 'hk-delegate-vertical-scrolling';
const BOTH_CLASSES = `${END_PADDING_CLASS} ${DELEGATE_VERTICAL_SCROLLING_CLASS}`;

@Component({
  selector: 'mx-split-form-dialog-section',
  host: {
    /*
      For STAFFUI-1563:  The vertical gaps aren't quite right.  The <form> has a py-2 to add enough padding so that floating labels aren't cut off on the
      top-most element(s), and so that the bottom part of the outline isn't cut off on the bottom-most element(s).  We want this host gap to be gap-y-6,
      but it's shaved down to compensate for the py-2.

      But... the problem is when we don't have aboveForm and/or belowForm content.  In that case, the gap-y-4 here doesn't apply, and we should be reducing
      the gap height above/below this component instead.  That can probably all be worked out with :has() styling in the main stylesheet (instead of here),
      but it'll take some thought.
    */
    class: 'hk-delegate-vertical-scrolling gap-y-4 [&>form>*]:flex [&>form>*]:flex-col [&>form>*]:gap-y-5'
  },
  template: `
    <div class="flex flex-col gap-y-6 empty:hidden {{ additionalAboveFormClasses }}">
      <ng-content select="[aboveForm]"/>
    </div>
    <form #tabForm [formGroup]="formGroup" class="py-2 empty:hidden {{ additionalFormClasses }}" [ngClass]="{'pe-3': isFormOverflowing}">
      <ng-content/>
    </form>

    <div class="flex flex-col gap-y-6 empty:hidden {{ additionalBelowFormClasses }}">
      <ng-content select="[belowForm]"/>
    </div>
  `,
  /*
    eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    -- Need to be able to dynamically detect overflow for scroll styling
  */
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [ReactiveFormsModule, NgClass]
})
export class SplitFormDialogSectionComponent implements AfterViewInit, OnDestroy {

  @Input() public formGroup: ExtendedFormGroup;

  private scrollSection: DelegateVerticalScrollingPosition;

  @Input()
  public set delegateVerticalScrolling(position: DelegateVerticalScrollingPosition) {
    this.scrollSection = position;
    switch (position) {
      // If we're delegating vertical scrolling directly to one of the main content sections, we want to delegate scrolling and NOT show additional end padding.
      case 'above-form':
        this.additionalAboveFormClasses = DELEGATE_VERTICAL_SCROLLING_CLASS;
        break;
      case 'on-form':
        this.additionalFormClasses = DELEGATE_VERTICAL_SCROLLING_CLASS;
        break;
      case 'below-form':
        this.additionalBelowFormClasses = DELEGATE_VERTICAL_SCROLLING_CLASS;
        break;

      // If we're delegating vertical scrolling to inside one of the main content sections, we want to delegate scrolling and ALSO show additional end padding.
      case 'on-descendant-above-form':
        this.additionalAboveFormClasses = BOTH_CLASSES;
        break;
      case 'on-descendant-of-form':
        this.additionalFormClasses = BOTH_CLASSES;
        break;
      case 'on-descendant-below-form':
        this.additionalBelowFormClasses = BOTH_CLASSES;
        break;
      default:
        break;
    }
  }

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private formRef?: ElementRef<HTMLFormElement>;
  private formElement?: Element;
  private resizeObserver!: ResizeObserver;
  public isFormOverflowing: boolean = false;

  @ViewChild('tabForm', {read: ElementRef})
  private set formReference(value: ElementRef | undefined) {
    this.formRef = value;
    this.changeDetector.markForCheck();
  }

  public additionalAboveFormClasses: string = END_PADDING_CLASS;
  public additionalFormClasses: string = END_PADDING_CLASS;
  public additionalBelowFormClasses: string = END_PADDING_CLASS;

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(([entry]) => {
      this.formElement = entry.target;

      // The result of this is used to dynamically add some extra padding to the right side of the form.  Only when the form's content is overflowing and it
      // needs to scroll.  This is just a nice QoL thing to keep the content from butting right up against the scroll bar.
      this.isFormOverflowing = isDefined(this.formElement) ? this.formElement.scrollHeight > this.formElement.clientHeight : false;
    });

    // Set up resizeObserver to observe our form element
    if (isDefined(this.formRef) && isDefined(this.formRef.nativeElement)) {
      this.resizeObserver.observe(this.formRef?.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
